import { DetailsInfo } from '@/components';
import { FALLBACK_TEXT, dateFormat, datetimeFormat } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { type BatchModel, BatchTypeLabelModifier } from '../../_models/batch';
import {
  convertBatchStatus,
  convertBatchTarget,
} from '../../_utils/batch-converter';

type Props = {
  batch: z.infer<typeof BatchModel>;
};

export const BatchResult: FC<Props> = ({ batch }) => {
  const { t } = useTranslation();
  return (
    <DetailsInfo
      items={[
        {
          label: t('batch.type'),
          value: BatchTypeLabelModifier[batch.type],
        },
        {
          label: t('batch.target'),
          value: convertBatchTarget(batch.target, batch.type, batch.params),
        },
        {
          label: t('batch.status'),
          value: convertBatchStatus({
            type: batch.type,
            resultType: batch.taskResult.result?.batchResult,
            status: batch.status,
            processedCount: batch.processedCount,
            targetCount: batch.targetCount,
          }),
        },
        {
          label: t('batch.start-datetime'),
          value: datetimeFormat({
            datetime: batch.taskResult.dateCreated,
          }),
        },
        {
          label: t('batch.end-datetime'),
          value:
            batch.status === 'processing'
              ? FALLBACK_TEXT
              : datetimeFormat({
                  datetime: batch.taskResult.dateDone,
                }),
        },
        {
          label: t('batch.target-count'),
          value: batch.taskResult.result?.targetCount,
        },
        {
          label: t('batch.processed-count'),
          value: batch.taskResult.result?.bulkProcessedCount,
        },
        {
          label: t('batch.skipped-count'),
          value: batch.taskResult.result?.bulkSkippedCount,
        },
        ...(batch.hasDownloadFile
          ? [
              {
                label: t('batch.download-expires-at'),
                value: batch.downloadExpiresAt
                  ? dateFormat({
                      datetime: batch.downloadExpiresAt,
                    })
                  : FALLBACK_TEXT,
              },
              ...(batch.canDownloadFile
                ? []
                : [
                    {
                      label: t('batch.reason-for-download-termination'),
                      value: batch.isDownloadFileOutdated
                        ? t('batch.download-outdated')
                        : t('batch.version-outdated'),
                    },
                  ]),
            ]
          : []),
      ]}
    />
  );
};
